// src/components/products/components/ProductListItem.jsx

import React from 'react';
import { 
  EyeIcon, 
  EyeSlashIcon, 
  ChevronDownIcon, 
  ChevronUpIcon,
  ExclamationCircleIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import ProductForm from './ProductForm';

const ProductListItem = ({ 
  product, 
  isExpanded, 
  onExpand, 
  onSave, 
  onDelete, 
  onToggleStatus 
}) => {
  const missingDescription = !product.description || product.description.trim() === '';
  const isLive = product.status === 'live';
  
  // Format date as DD/MM/YY - handles Firestore Timestamps
  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';
    
    // Handle Firestore Timestamp object
    let date;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      // Fallback for Firestore timestamp with seconds
      date = new Date(timestamp.seconds * 1000);
    } else {
      // Regular date string or Date object
      date = new Date(timestamp);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) return 'No date';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };
  
  // Inline styles using CSS variables
  const styles = {
    container: {
      background: 'var(--Surface-Primary)',
      borderRadius: 'var(--radius-s)',
      border: '1px solid var(--Stroke-Light)',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s ease'
    },
    header: {
      padding: 'var(--spacing-s) var(--spacing-s)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease'
    },
    productImage: {
      width: '48px',
      height: '48px',
      borderRadius: 'var(--radius-xs)',
      overflow: 'hidden',
      background: 'var(--Grey-100)',
      flexShrink: 0
    },
    productTitle: {
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--Text-Primary)',
      fontSize: 'var(--font-size-body3)'
    },
    productDetails: {
      fontSize: 'var(--font-size-body4)',
      color: 'var(--Text-Secondary)',
      marginTop: '2px'
    },
    price: {
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--Text-Primary)',
      fontSize: 'var(--font-size-body3)'
    },
    statusBadge: {
      fontSize: 'var(--font-size-body5)',
      fontWeight: 'var(--font-weight-medium)',
      padding: '2px 8px',
      borderRadius: 'var(--radius-pill)'
    },
    warningBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '2px 8px',
      background: 'var(--Red-200)',
      color: 'var(--Red-700)',
      fontSize: 'var(--font-size-body5)',
      borderRadius: 'var(--radius-pill)'
    },
    expandedSection: {
      borderTop: '1px solid var(--Stroke-Light)'
    }
  };
  
  return (
    <div 
      style={styles.container}
      onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <div 
        style={styles.header}
        onClick={() => onExpand(product.id)}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--Surface-Hover)'}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-s)' }}>
          {/* Product Image */}
          <div style={styles.productImage}>
            {product.imageUrls && product.imageUrls.length > 0 ? (
              <img
                src={product.imageUrls[0]}
                alt={product.name || 'Product'}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <div style={{ 
                width: '100%', 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <PhotoIcon style={{ width: '20px', height: '20px', color: 'var(--Grey-400)' }} />
              </div>
            )}
          </div>
          
          {/* Product Info */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
              <h3 style={styles.productTitle}>
                {product.name || <span style={{ color: 'var(--Text-Tertiary)', fontStyle: 'italic' }}>Untitled Product</span>}
              </h3>
              {isLive && missingDescription && (
                <div style={styles.warningBadge}>
                  <ExclamationCircleIcon style={{ width: '12px', height: '12px' }} />
                  <span>No description</span>
                </div>
              )}
            </div>
            <p style={styles.productDetails}>
              <span style={{ color: 'var(--Text-Tertiary)' }}>Added {formatDate(product.createdAt)}</span>
            </p>
          </div>
        </div>
        
        {/* Right side controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-m)' }}>
          {/* Price */}
          <span style={styles.price}>
            {product.price ? `£${product.price}` : <span style={{ color: 'var(--Text-Tertiary)' }}>No price</span>}
          </span>
          
          {/* Status Toggle */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleStatus(product.id);
            }}
            style={{
              position: 'relative',
              display: 'inline-flex',
              height: '24px',
              width: '44px',
              alignItems: 'center',
              borderRadius: 'var(--radius-pill)',
              border: 'none',
              cursor: 'pointer',
              transition: 'background 0.3s ease',
              background: isLive ? 'var(--brand-gradient-4)' : 'var(--Grey-300)'
            }}
          >
            <span className="sr-only">Toggle status</span>
            <span style={{
              display: 'inline-flex',
              height: '20px',
              width: '20px',
              transform: isLive ? 'translateX(22px)' : 'translateX(2px)',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              background: 'var(--Surface-Primary)',
              boxShadow: 'var(--shadow-sm)',
              transition: 'transform 0.3s ease'
            }}>
              {isLive ? (
                <EyeIcon style={{ height: '12px', width: '12px', color: 'var(--Grey-900)' }} />
              ) : (
                <EyeSlashIcon style={{ height: '12px', width: '12px', color: 'var(--Grey-400)' }} />
              )}
            </span>
          </button>
          
          {/* Status Label */}
          <span style={{
            ...styles.statusBadge,
            background: isLive ? 'var(--brand-gradient-4)' : 'var(--Grey-100)',
            color: isLive ? 'var(--Grey-900)' : 'var(--Text-Secondary)'
          }}>
            {isLive ? 'Live' : 'Draft'}
          </span>
          
          {/* Expand Arrow */}
          {isExpanded ? (
            <ChevronUpIcon style={{ width: '20px', height: '20px', color: 'var(--Grey-400)' }} />
          ) : (
            <ChevronDownIcon style={{ width: '20px', height: '20px', color: 'var(--Grey-400)' }} />
          )}
        </div>
      </div>
      
      {/* Expanded Form */}
      {isExpanded && (
        <div style={styles.expandedSection}>
          <ProductForm
            product={product}
            onSave={onSave}
            onCancel={() => onExpand(null)}
            onDelete={onDelete}
          />
        </div>
      )}
    </div>
  );
};

export default ProductListItem;